import { useMutation, useQuery, useQueryClient } from "react-query";
import http from "shared/http";

export interface PinnedReport {
  resourceId: number;
  title: string;
  summary: string;
  defaultAgencyCode: string | null;
  defaultFiltersJSON: string | null;
  workspaceId: string;
  reportId: string;
  datasetId: string;
  requiresEffectiveIdentity: boolean;
  defaultPageName: string | null;
  pbiRoles: string | null;
  agencyCodes: string[];
}

export interface PinReportRequest {
  resourceId: number;
  defaultAgencyCode?: string | null;
  defaultFiltersJSON?: string | null;
}

// Queries
const fetchPinnedReports = async (userId: number): Promise<PinnedReport[]> => {
  const data = await http.fetchAsync(`pins/${userId}`);
  return (data as PinnedReport[]) ?? [];
};

export const useGetPinnedReportsQuery = (userId: number) => {
  return useQuery(["pins", userId], () => fetchPinnedReports(userId), {
    enabled: userId > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Mutations
export const usePinReportMutation = (userId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (request: PinReportRequest) => http.postAsync(`pins/${userId}`, request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["pins", userId]);
      },
    },
  );
};

export const useUnpinReportMutation = (userId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (resourceId: number) => http.deleteAsync(`pins/${userId}`, resourceId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["pins", userId]);
      },
    },
  );
};
